import {
  Box,
  Stepper,
  Typography,
  Step,
  StepLabel,
  Button,
} from "@mui/material";
import React, { useRef } from "react";
import { BankingDetails } from "../pages/BankingDetails";
import { SupportingDocumentation } from "../pages/SupportingDocumentation";
import { IdentifyPolicy } from "../pages/IdentifyPolicy";
import { SelectClaimingParty } from "../pages/SelectClaimingParty";
import { EventDates } from "../pages/EventDates";
import { Consent } from "../pages/Consent";
import { DNA } from "react-loader-spinner";
import { SuccessResponse } from "../types";
import useCheckMobileScreen from "../utils/checkMobile";

const steps = [
  "Identify policy",
  "Select claiming party",
  "Enter event dates",
  "Banking details for refunds",
  "Supporting documentation",
  "Consent",
];

export function ClaimsStepper() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [totalFormData, setTotalFormData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [dataResponse, setDataResponse] =
    React.useState<SuccessResponse | null>(null);
  const stepperRef = useRef();

  // Banking details step can be skipped (optional)
  const isStepOptional = (step: number) => {
    return step === -1;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);

    if (activeStep === steps.length - 1) {
      console.log("formData", totalFormData);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  function handleDataSubmitted(
    data: Record<string, string>,
    step: string
  ): void {
    setTotalFormData((prevData) => ({
      ...prevData,
      [step]: data,
    }));
  }

  function handleDocumentsSubmitted(
    data: { fileName: string; file: string }[],
    step: string
  ): void {
    const newData = {
      ...totalFormData,
      [step]: data,
    };
    setTotalFormData(newData);
  }

  function handleFinalSubmit(consentArg?: {
    termsTimestamp?: string;
    popiaTimestamp?: string;
  }) {
    const tf = totalFormData as Record<string, any>;
    const eventDates = (tf["event-dates"] as Record<string, any>) || {};
    const policyholder =
      (tf["policyholder-details"] as Record<string, any>) || {};
    const identify = (tf["identify-policy"] as Record<string, any>) || {};
    const banking = (tf["banking-details"] as Record<string, any>) || {};
    const claimInfo = (tf["claim-info"] as Record<string, any>) || {};
    // Always include broker number in submit payload; default to empty string
    const brokerNumber: string = (identify?.brokerNumber as string) || "";
    const requiredDocs = (tf["supporting-documentation-required"] ||
      []) as string[];
    const uploadedDocs =
      (tf["supporting-documentation"] as Array<{
        fileName: string;
        file: string;
      }>) || [];
    const consentObj = (tf["consent"] as Record<string, any>) || {};
    const selectClaimingParty =
      (tf["select-claiming-party"] as Record<string, any>) || {};
    const claimingPartyId = selectClaimingParty.claimingPartyId || "";
    // intRiskID is returned by getSiragoDependants for both dependants and policyholder
    const intRiskID = claimingPartyId || identify.policyholderRiskId || "";

    // Build payload in the original structure; empty strings for unknowns
    const payload: Record<string, any> = {
      broker_number: brokerNumber,
      intRiskID,
      "medical-event-info": {
        "completing-application": "",
        "medical-event-details": "Claim submitted via Sirago Claim portal",
        "admission-date": eventDates.admissionDate || "",
        "discharge-date": eventDates.dischargeDate || "",
      },
      "policyholder-details": {
        name: policyholder.name || "",
        surname: policyholder.surname || "",
        "id-number":
          identify.resolvedIdNumber ||
          identify.idNumber ||
          policyholder["id-number"] ||
          "",
        "policy-number":
          identify.resolvedPolicyNumber || policyholder["policy-number"] || "",
        "contact-email":
          identify.resolvedEmail || policyholder["contact-email"] || "",
        "contact-work-number":
          identify.resolvedMobileNo ||
          policyholder["contact-work-number"] ||
          "",
        "contact-phone":
          identify.resolvedMobileNo || policyholder["contact-phone"] || "",
        "residential-address-line-1":
          identify.resolvedAddressLine1 ||
          policyholder["residential-address-line-1"] ||
          "",
        "residential-address-line-2":
          identify.resolvedAddressLine2 ||
          policyholder["residential-address-line-2"] ||
          "",
        "residential-city":
          identify.resolvedCity || policyholder["residential-city"] || "",
        "residential-zip-code":
          identify.resolvedZipCode ||
          policyholder["residential-zip-code"] ||
          "",
        "postal-address-line-1": policyholder["postal-address-line-1"] || "",
        "postal-address-line-2": policyholder["postal-address-line-2"] || "",
        "postal-city": policyholder["postal-city"] || "",
        "postal-zip-code": policyholder["postal-zip-code"] || "",
        "date-of-birth": policyholder["date-of-birth"] || "",
      },
      "banking-details": {
        name: banking.name || "",
        accountNumber: banking.accountNumber || "",
        bank: banking.bank || "",
        accountType: banking.accountType || "",
      },
      "claim-info": {
        "gap-cover-claim": claimInfo["gap-cover-claim"] || "",
        "cancer-boost": claimInfo["cancer-boost"] || "",
      },
      "supporting-documentation-required": requiredDocs,
      "supporting-documentation": uploadedDocs.map((d) => ({
        fileName: d.fileName || "",
        file: d.file || "",
      })),
    };
    // Top-level consent timestamps
    payload["popia-consent-timestamp"] =
      consentArg?.popiaTimestamp || consentObj["popia-consent-timestamp"] || "";
    payload["terms-consent-timestamp"] =
      consentArg?.termsTimestamp || consentObj["terms-consent-timestamp"] || "";
    setIsLoading(true);
    fetch("https://siragoapiprod.azure-api.net/siragoGap?cmd=registerClaim", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        const dataResponse = data as SuccessResponse;
        setDataResponse(dataResponse);
        setIsLoading(false);
        setActiveStep(6);
      })
      .catch((error) => {
        console.error("Error:", error);
        setIsLoading(false);
      });
  }

  return (
    <Box sx={{ width: "100%", p: { xs: 2, sm: 4 }, mt: 8, overflow: "auto" }}>
      <Stepper
        sx={{ mt: 2, mb: 2, overflow: "auto" }}
        activeStep={activeStep}
        orientation={useCheckMobileScreen() ? "vertical" : "horizontal"}
      >
        {steps.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: {
            optional?: React.ReactNode;
          } = {};
          if (isStepOptional(index)) {
            labelProps.optional = (
              <Typography variant="caption">Optional</Typography>
            );
          }
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <DNA
            visible={true}
            height="300"
            width="200"
            ariaLabel="line-wave-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
          <Typography sx={{ mt: 2, mb: 1 }}>
            Please wait while we process your claim
          </Typography>
        </div>
      )}
      {!isLoading && (
        <div>
          {activeStep === steps.length ? (
            <React.Fragment>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {dataResponse?.result === true && (
                  <img
                    style={{
                      height: "auto",
                      width: "40vw",
                    }}
                    src={require("../assets/success.gif")}
                    alt="success..."
                  />
                )}
                {dataResponse?.result === false && (
                  // <img
                  //   style={{
                  //     margin: "20px 0",
                  //     height: "auto",
                  //     width: "15vw",
                  //   }}
                  //   src={require("../assets/warning.jpg")}
                  //   alt="warning..."
                  // />
                  //always show success since we'll be creating FD tickets if ski fails
                  <img
                    style={{
                      height: "auto",
                      width: "40vw",
                    }}
                    src={require("../assets/success.gif")}
                    alt="success..."
                  />
                )}
                {dataResponse?.result === true &&
                  (dataResponse?.claimRef ? (
                    <>
                      <Typography sx={{ mt: 2, mb: 1, textAlign: "center" }}>
                        {`Thanks, your claim was submitted successfully. Claim number: ${
                          dataResponse.claimRef
                        } reference number: ${dataResponse?.ticketRef || ""}`}
                      </Typography>
                      <Typography sx={{ mt: 1, mb: 1, textAlign: "center" }}>
                        What&apos;s next: we&apos;ll review your documents and
                        may contact you if anything else is needed. For any
                        queries, quote the reference number in your email
                        subject or to the consultant.
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ mt: 2, mb: 1, textAlign: "center" }}>
                      {`Thank you for your submission. Your reference number is ${
                        dataResponse?.ticketRef || ""
                      }. For any queries, quote the Reference number in your email subject or to the consultant.`}
                    </Typography>
                  ))}
                {dataResponse?.result === false && (
                  <Typography sx={{ mt: 2, mb: 1, textAlign: "center" }}>
                    Thank you for your submission. Your reference number is{" "}
                    {dataResponse?.ticketRef || ""}. For any queries, please
                    send an email to: claims@sirago.co.za and quote the
                    reference number in your email subject or to the consultant.
                  </Typography>
                )}
                <Box sx={{ display: "none", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button onClick={handleReset}>Reset</Button>
                </Box>
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {/* <Typography sx={{ mt: 2, mb: 1 }}>Step {activeStep + 1}</Typography> */}
              {activeStep === 0 && (
                <IdentifyPolicy
                  handleNext={handleNext}
                  handleBack={handleBack}
                  activeStep={activeStep}
                  isStepOptional={isStepOptional}
                  steps={steps}
                  totalFormData={totalFormData}
                  handleDataSubmitted={handleDataSubmitted}
                />
              )}
              {activeStep === 1 && (
                <SelectClaimingParty
                  handleNext={handleNext}
                  handleBack={handleBack}
                  activeStep={activeStep}
                  isStepOptional={isStepOptional}
                  steps={steps}
                  totalFormData={totalFormData}
                  handleDataSubmitted={handleDataSubmitted}
                />
              )}
              {activeStep === 2 && (
                <EventDates
                  handleNext={handleNext}
                  handleBack={handleBack}
                  activeStep={activeStep}
                  isStepOptional={isStepOptional}
                  steps={steps}
                  totalFormData={totalFormData}
                  handleDataSubmitted={handleDataSubmitted}
                />
              )}
              {activeStep === 3 && (
                <BankingDetails
                  handleNext={handleNext}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  activeStep={activeStep}
                  isStepOptional={isStepOptional}
                  isStepSkipped={isStepSkipped}
                  steps={steps}
                  totalFormData={totalFormData}
                  handleDataSubmitted={handleDataSubmitted}
                />
              )}
              {activeStep === 4 && (
                <SupportingDocumentation
                  handleNext={handleNext}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  activeStep={activeStep}
                  isStepOptional={isStepOptional}
                  steps={steps}
                  totalFormData={totalFormData}
                  handleDataSubmitted={handleDocumentsSubmitted}
                />
              )}
              {activeStep === 5 && (
                <Consent
                  handleNext={handleNext}
                  handleBack={handleBack}
                  activeStep={activeStep}
                  steps={steps}
                  onSubmit={handleFinalSubmit}
                  handleDataSubmitted={handleDataSubmitted}
                />
              )}
            </React.Fragment>
          )}
        </div>
      )}
    </Box>
  );
}
export default ClaimsStepper;
